/**
 * useEnrichedTickets — loads the enriched SIM ticket export (org_analysed_tickets.json)
 * from S3 via the shared useS3Dataset primitive, using the same fetch mechanism as the
 * existing run-data dataset.
 *
 * Path (alpha): s3://op-excellence-alpha/oe-rpt-tables-data-export/sim_tickets_analysed/run-data
 *
 * The enriched file is a NEAR-SUPERSET of run-data (see enrichedTicketExport.types.ts — the
 * first-response SLA inputs are intentionally not carried), so consumers that only need the
 * shared base fields can also read it. This hook exposes the full EnrichedTicketExport
 * (tickets + oeOpportunities + meta).
 *
 * Graceful behaviour: while there is no usable data the hook reports 'error' (initial fetch/parse
 * failure or an absent object) so callers fall back to the run-data path (useSimHealthPreview).
 * Once a valid export has loaded it is served ('finished') even through a later failed or in-flight
 * background refetch — usable data is never discarded for a transient error/loading state.
 */
import { useMemo } from 'react';
import { FinTechOpsCategories, TICKETS_ANALYSED_LAST_UPDATED_AT_S3_PREFIX, TICKETS_ANALYSED_S3_PREFIX } from '../../FinTechOpsConstants';
import { useS3Dataset } from '../../shared/hooks/useS3Dataset';
import { DashboardInsight, EnrichedTicket, EnrichedTicketExport, OeOpportunity } from '../interfaces/enrichedTicketExport.types';

export interface UseEnrichedTicketsResult {
  status: 'loading' | 'finished' | 'error';
  tickets: EnrichedTicket[];
  oeOpportunities: OeOpportunity[];
  generatedAt: string | undefined;
  lastUpdatedAt: string | null;
  /** LLM chart takeaways from meta.dashboardInsights (empty object when unavailable). */
  dashboardInsights: Record<string, DashboardInsight>;
}

const EMPTY: EnrichedTicketExport = {
  meta: { schemaVersion: 0, generatedAt: '', sourceSnapshot: '', orgScope: '', ticketCount: 0 },
  tickets: [],
  oeOpportunities: []
};

/**
 * getS3Data returns an array of file payloads (one per run-* object under the prefix).
 * Our enriched export is a single `run-data` JSON object, so normalise the raw payload
 * into a single EnrichedTicketExport regardless of whether it arrives wrapped in an array.
 *
 * THROWS on structurally-invalid input (missing/non-object, or meta with schemaVersion <= 0).
 * This is deliberate: useS3Dataset only caches a payload AFTER parse() returns successfully, and
 * surfaces a thrown parse error via its `error` state. If we instead returned an EMPTY doc for a
 * transiently corrupt/partial S3 object, that EMPTY would be cached for the full TTL and keep the
 * hook stuck reporting 'error' (schemaVersion 0) even after the real file lands. Throwing lets a
 * subsequent good fetch recover.
 */
export const parseEnriched = (raw: unknown): EnrichedTicketExport => {
  const obj = Array.isArray(raw) ? raw[0] : raw;
  if (!obj || typeof obj !== 'object') {
    throw new Error('Enriched ticket export is missing or not an object');
  }
  const doc = obj as Partial<EnrichedTicketExport>;
  if (!doc.meta || !(doc.meta.schemaVersion > 0)) {
    throw new Error('Enriched ticket export has invalid or missing meta (schemaVersion must be > 0)');
  }
  return {
    meta: doc.meta,
    tickets: Array.isArray(doc.tickets) ? doc.tickets : [],
    oeOpportunities: Array.isArray(doc.oeOpportunities) ? doc.oeOpportunities : []
  };
};

export const useEnrichedTickets = (): UseEnrichedTicketsResult => {
  const { data, error, lastUpdatedAt } = useS3Dataset<unknown, EnrichedTicketExport>({
    prefix: TICKETS_ANALYSED_S3_PREFIX,
    lastUpdatedPrefix: TICKETS_ANALYSED_LAST_UPDATED_AT_S3_PREFIX,
    category: FinTechOpsCategories.tickets,
    parse: parseEnriched
  });

  const doc = data ?? EMPTY;

  return useMemo(
    () => ({
      // Status priority (in order):
      //   1. data present            → 'finished' — usable data ALWAYS wins, even through a later
      //      failed/in-flight background refetch (useS3Dataset keeps prior `data` on refetch), so a
      //      TTL-refresh never flips a populated tab back to a spinner/error or makes callers
      //      discard good tickets to fall back to run-data.
      //   2. error set (no data)     → 'error' — a GENUINE settled failure (initial fetch/parse
      //      error). We key on `error`, NOT on "no data && !loading", because on mount with an
      //      EXPIRED cache entry useS3Dataset inits loading=false (the stale entry is still in the
      //      Map) with data=undefined — that transient pre-refetch window must read as 'loading',
      //      not flash a spurious 'error'.
      //   3. otherwise               → 'loading' — not yet fetched (incl. the expired-cache window).
      // parseEnriched THROWS on invalid payloads (never cached), so `data` is only ever a valid doc;
      status: data !== undefined ? 'finished' : error ? 'error' : 'loading',
      tickets: doc.tickets,
      oeOpportunities: doc.oeOpportunities,
      generatedAt: doc.meta?.generatedAt || undefined,
      lastUpdatedAt,
      dashboardInsights: doc.meta?.dashboardInsights ?? {}
    }),
    [error, data, lastUpdatedAt]
  );
};
